import React from 'react';
import { Navigate } from 'react-router-dom';
import { Box, CircularProgress, Alert, AlertTitle, Button } from '@mui/material';
import { usePermissions } from '../hooks/usePermissions';
import { useAuth } from '../contexts/AuthContext';

/**
 * Компонент для защиты роутов на основе прав доступа
 * @param {Object} props
 * @param {React.ReactNode} props.children - Дочерние компоненты
 * @param {string} props.resource - Ресурс для проверки (например, 'addresses')
 * @param {string} props.action - Действие для проверки (например, 'view')
 * @param {string} props.redirectTo - Путь для редиректа при отсутствии прав (по умолчанию '/dashboard')
 */
const ProtectedRoute = ({ 
  children, 
  resource, 
  action = 'view',
  roles,
  redirectTo = '/dashboard' 
}) => {
  const { currentUser } = useAuth();
  const { hasPermission, loading } = usePermissions();

  // Показываем загрузку пока проверяем права
  if (loading) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        minHeight="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  // Куратор имеет доступ ко всему
  if (currentUser?.role === 'curator') {
    return children;
  }

  if (Array.isArray(roles) && roles.length > 0 && !roles.includes(currentUser?.role)) {
    return <Navigate to={redirectTo} replace />;
  }

  // Проверяем права доступа
  const hasAccess = hasPermission(resource, action);

  if (!hasAccess) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        minHeight="100vh"
        p={3}
      >
        <Alert 
          severity="error" 
          sx={{ maxWidth: 600 }}
          action={
            <Button 
              color="inherit" 
              size="small"
              onClick={() => window.location.href = redirectTo}
            >
              На главную
            </Button>
          }
        >
          <AlertTitle>Доступ запрещен</AlertTitle>
          У вас нет прав для просмотра этой страницы.
          <br />
          Требуется право: <strong>{resource}.{action}</strong>
        </Alert>
      </Box>
    );
  }

  return children;
};

export default ProtectedRoute;
